import { useMemo, useCallback } from "react";

import { toastify } from "@vendors";
import { useKinndomContext } from "@contexts/KinndomContext";
import { useForm, useFormConfig } from "@hooks";
import { interpolate, redirectTo, Router } from "@utils";
// import { useArchetypes } from '@hooks';

import FormBody from "@components/form/FormBody";

import ModalHeader from "@components/modal/ModalHeader";
import ModalBody from "@components/modal/ModalBody";
import ModalFooter from "@components/modal/ModalFooter";
import ModalCancelButton from "@components/modal/ModalCancelButton";
import ModalSubmitButton from "@components/modal/ModalSubmitButton";

const MODAL_FIELD_CONFIG_OVERRIDES = {
  trackChanges: false,
};

const defaultConfig = {
  requestMethod: "POST",
  submitButtonLabel: "Create",
};

export default function ModalForm(props) {
  const {
    data,
    mx,
    onSubmitSuccess,
  } = props;

  const kinndom = useKinndomContext();

  const interpolationData = useMemo(() => ({
    kinndomKey: window.appContext.kinndomKey,
    defaultWebsiteId: window.appContext.defaultWebsiteId,
    defaultWebsiteBaseUrl: window.appContext.defaultWebsiteBaseUrl,

    kinndom: kinndom,
    config: kinndom.configuration,
    session: {
      currentUserId: window.appContext.currentUserId,
      currentUserIpAddress: window.appContext.currentUserIpAddress,
      currentUserAgent: window.appContext.currentUserAgent,
      isCurrentUserAdmin: window.appContext.isCurrentUserAdmin,
    },
    ...data,
  }), [kinndom, data]);

  const config = interpolate({ ...defaultConfig, ...props.config }, interpolationData);
  const defaultValues = interpolate(config.defaultValues, interpolationData);

  // const { formFields } = useArchetypes(config.path, {
  //   formFields: config.fields,
  // });

  const {
    initialFormValues,
    fieldValidations,
  } = useFormConfig({
    fields: config.fields,
    data: {
      ...defaultValues,
      ...data,
    },
  });

  const formBody = useMemo(() => config.fields.map((row) => {
    return row.map((field) => ({
      ...field,
      config: {
        ...field.config,
        ...MODAL_FIELD_CONFIG_OVERRIDES,
      },
    }));
  }), [config.fields]);

  /**************************************************************************************************/

  const handleFormSubmitSuccess = useCallback((data) => {
    if (onSubmitSuccess) {
      onSubmitSuccess(data);
      return;
    }

    if (config.callbackAction === "redirect" && config.callbackPath) {
      const path = config.callbackPath.replace("%{record_id}", data?.id || "");
      redirectTo(Router.buildUrlFor(path));
    } else if (config.callbackAction === "redirectToNewRecord" && data?.id) {
      redirectTo(Router.buildUrlFor(`${config.path}/${data.id}`));
    } else {
      location.reload();
    }
  }, [onSubmitSuccess, config.callbackAction, config.callbackPath, config.path]);

  const handleFormSubmitFailure = (error) => {
    if (error) {
      toastify.error(error.message);
    }
  };

  const {
    formRef,
    formState,
    formValues,
    formErrors,
    handleFormFieldChange,
    handleFormSubmit,
  } = useForm({
    path: config.path,
    requestMethod: config.requestMethod,
    resource: config.resource,
    params: {
      current_user_id: window.appContext.currentUserId,
    },
    initialFormValues: initialFormValues,
    // additionalFormValues: defaultValues,
    fieldValidations: fieldValidations,
    onSubmitSuccess: handleFormSubmitSuccess,
    onSubmitFailure: handleFormSubmitFailure,
  });

  const displayFooter = Boolean(config.hideFooter) === false;

  /**************************************************************************************************/

  return (
    <form ref={formRef} onSubmit={handleFormSubmit}>
      <ModalHeader
        title={config.title}
        titleStartIcon={config.titleStartIcon}
        titleEndIcon={config.titleEndIcon}
        onClickCloseButton={mx.close}
      />
      <ModalBody>
        <FormBody
          resource={config.resource}
          path={config.path}
          body={formBody}
          formValues={formValues}
          initialFormValues={initialFormValues}
          formErrors={formErrors}
          handleFormFieldChange={handleFormFieldChange}
          isAutofocusable={true}
        />
      </ModalBody>
      {displayFooter && (
        <ModalFooter>
          <ModalCancelButton
            onClick={mx.close}
          />
          <ModalSubmitButton
            label={config.submitButtonLabel}
            onClick={handleFormSubmit}
            isLoading={formState.isSubmitting}
          />
        </ModalFooter>
      )}
    </form>
  );
}
